/* FILE: ./frontend/src/contexts/AuthContext.jsx */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutState, setLogoutState] = useState({ loading: false, error: null });

  // --- Supplier Role Management ---
  const [isSupplier, setIsSupplier] = useState(false);
  const [supplierId, setSupplierId] = useState(null);

  // ⚡ FIX: Isolated Data Loader. Guaranteed to always unlock the UI.
  const loadSessionData = async (sessionUser) => {
    if (!sessionUser) {
        setUser(null);
        setUserProfile(null);
        setIsSupplier(false);
        setSupplierId(null);
        setLoading(false);
        return;
    }

    try {
        setUser(sessionUser);
        // Run fetches in parallel, catch individual errors so one failure doesn't break the app
        await Promise.all([
            fetchUserProfile(sessionUser.id).catch(e => console.error("Profile Error:", e)),
            checkSupplierStatus(sessionUser.id).catch(e => console.error("Supplier Error:", e))
        ]);
    } catch (err) {
        console.error("Session data load failed:", err);
    } finally {
        setLoading(false); // ⚡ ALWAYS release the lock
    }
  };

  // ⚡ FIX: Properly bound the auth listener to the React lifecycle so it doesn't get lost
  useEffect(() => {
    let mounted = true;
    let subscription = null;

    const setupInitialSession = async () => {
      // ⚡ CIRCUIT BREAKER: Never let the UI hang forever.
      const failSafe = setTimeout(() => {
          if (mounted && loading) {
              console.warn("Auth initialization timed out. Forcing UI unlock.");
              setLoading(false);
          }
      }, 4000);

      try {
        console.log('🔄 Initializing authentication...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;

        console.log('📋 Initial session:', session ? 'User logged in' : 'No user');
        await loadSessionData(session?.user);
      } catch (error) {
        console.error('❌ Auth initialization failed:', error);
        if (mounted) setLoading(false);
      } finally {
        clearTimeout(failSafe);
      }
    };

    setupInitialSession();

    // ⚡ FIX: This listener now runs synchronously in the useEffect and correctly updates React State
    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event, session ? 'User present' : 'No user');
      
      if (event === 'INITIAL_SESSION') return; // Handled by setupInitialSession
      
      if (event === 'SIGNED_OUT') {
          if (mounted) loadSessionData(null);
      } else if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')) {
          if (mounted) loadSessionData(session.user);
      }
    });

    subscription = data.subscription;

    // ⚡ FIX: React now successfully receives the unsubscribe function on unmount
    return () => {
      mounted = false;
      if (subscription) subscription.unsubscribe();
    };
  
  }, []);

  const fetchUserProfile = async (userId) => {
    try {
      console.log('📋 Fetching user profile for:', userId);
      
      const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

      if (error) console.error('❌ Profile fetch error:', error);

      if (!data) {
        await createDefaultUserProfile(userId);
      } else {
        console.log('✅ User profile loaded');
        setUserProfile(data);
      }
    } catch (error) {
      console.error('❌ Profile fetch failed:', error);
    }
  };

  const createDefaultUserProfile = async (userId) => {
    try {
      // ⚡ FIX: Aligned strictly with actual profiles table schema
      const { data, error } = await supabase.from('profiles').insert([{
            id: userId,
            company_name: 'New User',
            contact_person: 'User',
            role: 'buyer',
            subscription_tier: 'free',
            buyer_tier: 'free',
            buyer_volume_tier: 'standard',
            supplier_tier: 'standard',
            connection_credits: 0,
            ai_audit_credits: 0,
            has_analytics: false,
            subscription_status: 'active'
          }]).select().single();
          
      if (error) throw error;
      
      setUserProfile(data);
      console.log('✅ Default profile created');
    } catch (error) {
      console.warn('⚠️ Default profile creation warning:', error.message);
    }
  };

  const checkSupplierStatus = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking supplier status:', error);
        return;
      }

      if (data) {
        console.log('✅ User is a Supplier:', data.id);
        setIsSupplier(true);
        setSupplierId(data.id);
      } else {
        console.log('ℹ️ User is a Buyer (No Supplier Profile)');
        setIsSupplier(false);
        setSupplierId(null);
      }
    } catch (error) {
      console.error('Supplier check failed:', error);
    }
  };

  const refreshContext = async () => {
    if (user) {
      await loadSessionData(user);
    }
  };

  const signUp = async (email, password, userData) => {
    try {
      console.log('👤 User registration started:', email);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            company_name: userData.company_name,
            contact_person: userData.contact_person,
            phone: userData.phone
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        // ⚡ FIX: Sync the signUp method strictly with existing database columns
        const { error: profileError } = await supabase
          .from('profiles') 
          .insert([
            {
              id: data.user.id,
              email: email,
              role: 'buyer',
              company_name: userData.company_name,
              contact_person: userData.contact_person,
              phone: userData.phone,
              billing_address: userData.billing_address,
              subscription_tier: 'free',
              buyer_tier: 'free',
              buyer_volume_tier: 'standard',
              supplier_tier: 'standard',
              connection_credits: 0,
              ai_audit_credits: 0,
              has_analytics: false,
              subscription_status: 'active'
            }
          ]);
          
        if (profileError) {
          console.warn('⚠️ Profile creation warning:', profileError.message);
        }
      }

      console.log('✅ Registration successful');
      return { data, error: null };
    } catch (error) {
      console.error('❌ Registration failed:', error);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      console.log('🔐 Login attempt:', email);
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      if (data.user) {
          console.log("⏳ Waiting for role assignment...");
          await loadSessionData(data.user);
          console.log("✅ Roles assigned. Proceeding.");
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('❌ Login failed:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = useCallback(async () => {
    setLogoutState({ loading: true, error: null });
    
    try {
      console.log('🚪 Logout process started');
      
      // 1. Clear Local Storage
      const sensitiveKeys = Object.keys(localStorage).filter(key => 
        key.includes('supabase') || key.includes('auth') || key.includes('user')
      );
      sensitiveKeys.forEach(key => localStorage.removeItem(key));
      sessionStorage.clear();
      localStorage.clear(); 
      
      // 2. Clear local state
      setUser(null);
      setUserProfile(null);
      setIsSupplier(false);
      setSupplierId(null);

      // 3. Supabase logout
      await supabase.auth.signOut();
      
      console.log('✅ Logout successful');
      setLogoutState({ loading: false, error: null });
      
      // 4. Force browser refresh to clear memory
      window.location.href = '/login'; 
      return { error: null };
      
    } catch (error) {
      console.error('❌ Logout failed:', error);
      setLogoutState({ loading: false, error: error.message });
      
      setUser(null);
      window.location.href = '/login';
      return { error };
    }
  }, []);

  const value = {
    user,
    userProfile,
    isSupplier, 
    supplierId, 
    signUp,
    signIn,
    signOut,
    loading,
    logoutState,
    isAuthenticated: !!user,
    refreshContext
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};